import React, { useContext } from 'react'

import { css } from 'design'
import { Button, buttonStyles } from 'primitives/button'
import * as DropdownMenu from 'primitives/dropdownmenu'
import { cn } from 'util/cn'

import { useCallManager } from '../../../contexts/call_manager_context'
import { DevicesContext } from '../../../contexts/devices_context'
import { useParticipantManager } from '../../../contexts/participant_manager_context'
import { AppErrorMessage } from '../../../controls/errors/app_error_message'
import { useSetError } from '../../../hooks/use_set_error'
import { CheckIcon, SettingsIcon } from '../../../images/index'
import { isLiveChatApp } from '../../../reduxes/slices/live_chat_app/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import { sidebarButtonStyles } from '../../topics/detail/components/sidebar_button'
import { TRANSIENT_STATES } from '../call_manager/connection_status_reducer'
import { LocalTrackData } from '../tracks/use_local_tracks'

type Props = {
  disableTriggerStyles?: boolean
} & React.HTMLAttributes<HTMLButtonElement>

export const DeviceSelectors = ({
  disableTriggerStyles = false,
  ...props
}: Props) => {
  const {
    activeCall: { connectionStatus },
  } = useCallManager()
  const { localTrackData } = useParticipantManager()
  const { askForPermission } = useContext(DevicesContext)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const {
    audioDeviceId,
    videoDeviceId,
    audioOutputDeviceId,
    audioDevices,
    videoDevices,
    audioOutputDevices,
    hasAudioDevices,
    hasVideoDevices,
    hasAudioOutputDevices,
    handleAudioDeviceChange,
    handleVideoDeviceChange,
    handleAudioOutputDeviceChange,
  } = localTrackData || ({} as LocalTrackData)
  const listOfAudioDevices = Object.values(audioDevices)
  const listOfVideoDevices = Object.values(videoDevices)
  const listOfAudioOutputDevices = Object.values(audioOutputDevices)
  const disabled =
    !!connectionStatus && TRANSIENT_STATES.includes(connectionStatus)
  const setError = useSetError()
  const { speakerSupport, permissionStates } = useContext(DevicesContext)
  const onAudioDeviceChange = (deviceId: string | null) => {
    handleAudioDeviceChange(deviceId).catch((error) => {
      setError({ domId: DEVICES_DROPDOWN_TRIGGER_ID, error: error.message })
    })
  }

  const onVideoDeviceChange = (deviceId: string | null) => {
    handleVideoDeviceChange(deviceId).catch((error) => {
      setError({ domId: DEVICES_DROPDOWN_TRIGGER_ID, error: error.message })
    })
  }

  const onAudioOutputDeviceChange = (deviceId: string | null) => {
    handleAudioOutputDeviceChange(deviceId).catch((error) => {
      setError({ domId: DEVICES_DROPDOWN_TRIGGER_ID, error: error.message })
    })
  }

  const onSelect = (event: Event) => {
    event.preventDefault()
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        {...props}
        id={DEVICES_DROPDOWN_TRIGGER_ID}
        className={cn(
          !disableTriggerStyles && buttonStyles(),
          !disableTriggerStyles && sidebarButtonStyles(),
          props.className,
        )}
      >
        <SettingsIcon width={25} height={25} />
        <span>Settings</span>
      </DropdownMenu.Trigger>

      <AppErrorMessage domId={DEVICES_DROPDOWN_TRIGGER_ID} />

      <DropdownMenu.Content
        className="topic__calls__deviceselectors"
        sideOffset={4}
        align="start"
        css={{
          minWidth: 250,
          ...(liveChatApp && { width: 250 }),
        }}
      >
        <DropdownMenu.Label>Microphone</DropdownMenu.Label>
        {hasAudioDevices ? (
          listOfAudioDevices.length ? (
            <DropdownMenu.RadioGroup
              // eslint-disable-next-line
              // @ts-ignore
              value={audioDeviceId}
              onValueChange={onAudioDeviceChange}
            >
              {
                <DropdownMenu.RadioItem
                  // eslint-disable-next-line
                  // @ts-ignore
                  value={null}
                  disabled={disabled}
                  className={radioItemStyles({
                    selected: audioDeviceId === null,
                  })}
                  onSelect={onSelect}
                >
                  <DropdownMenu.ItemIndicator>
                    <CheckIcon />
                  </DropdownMenu.ItemIndicator>
                  System Default
                </DropdownMenu.RadioItem>
              }
              {listOfAudioDevices.reduce(
                (
                  acc: any,
                  { deviceId, label }: MediaDeviceInfo,
                  index: number,
                ) => {
                  // ignore duplicate default device in chrome
                  if (deviceId !== 'default') {
                    acc.push(
                      <DropdownMenu.RadioItem
                        key={deviceId}
                        value={deviceId}
                        disabled={disabled}
                        className={radioItemStyles({
                          selected: deviceId === audioDeviceId,
                        })}
                        onSelect={onSelect}
                      >
                        <DropdownMenu.ItemIndicator>
                          <CheckIcon />
                        </DropdownMenu.ItemIndicator>
                        {label || `Microphone ${index + 1}`}
                      </DropdownMenu.RadioItem>,
                    )
                  }
                  return acc
                },
                [],
              )}
            </DropdownMenu.RadioGroup>
          ) : (
            <DropdownMenu.Label className={userFeedbackStyles()}>
              {permissionStates.microphone === 'prompt' ? (
                <Button onClick={() => askForPermission?.({ audio: true })}>
                  Request permission
                </Button>
              ) : (
                'Please grant permission'
              )}
            </DropdownMenu.Label>
          )
        ) : (
          <DropdownMenu.Label className={userFeedbackStyles()}>
            No devices detected
          </DropdownMenu.Label>
        )}

        <DropdownMenu.Separator />

        <DropdownMenu.Label>Camera</DropdownMenu.Label>
        {hasVideoDevices ? (
          listOfVideoDevices.length ? (
            <DropdownMenu.RadioGroup
              // eslint-disable-next-line
              // @ts-ignore
              value={videoDeviceId}
              onValueChange={onVideoDeviceChange}
            >
              {
                <DropdownMenu.RadioItem
                  // eslint-disable-next-line
                  // @ts-ignore
                  value={null}
                  disabled={disabled}
                  className={radioItemStyles({
                    selected: videoDeviceId === null,
                  })}
                  onSelect={onSelect}
                >
                  <DropdownMenu.ItemIndicator>
                    <CheckIcon />
                  </DropdownMenu.ItemIndicator>
                  System Default
                </DropdownMenu.RadioItem>
              }
              {listOfVideoDevices.map(
                ({ deviceId, label }: MediaDeviceInfo, index: number) => (
                  <DropdownMenu.RadioItem
                    key={deviceId}
                    value={deviceId}
                    disabled={disabled}
                    className={radioItemStyles({
                      selected: deviceId === videoDeviceId,
                    })}
                    onSelect={onSelect}
                  >
                    <DropdownMenu.ItemIndicator>
                      <CheckIcon />
                    </DropdownMenu.ItemIndicator>
                    {label || `Camera ${index + 1}`}
                  </DropdownMenu.RadioItem>
                ),
              )}
            </DropdownMenu.RadioGroup>
          ) : (
            <DropdownMenu.Label className={userFeedbackStyles()}>
              {permissionStates.camera === 'prompt' ? (
                <Button onClick={() => askForPermission?.({ video: true })}>
                  Request permission
                </Button>
              ) : (
                'Please grant permission'
              )}
            </DropdownMenu.Label>
          )
        ) : (
          <DropdownMenu.Label className={userFeedbackStyles()}>
            No devices detected
          </DropdownMenu.Label>
        )}

        <DropdownMenu.Separator />

        <DropdownMenu.Label>Speaker</DropdownMenu.Label>
        {speakerSupport ? (
          hasAudioOutputDevices ? (
            listOfAudioOutputDevices.length ? (
              <DropdownMenu.RadioGroup
                // eslint-disable-next-line
                // @ts-ignore
                value={audioOutputDeviceId}
                onValueChange={onAudioOutputDeviceChange}
              >
                {
                  <DropdownMenu.RadioItem
                    // eslint-disable-next-line
                    // @ts-ignore
                    value={null}
                    disabled={disabled}
                    className={radioItemStyles({
                      selected: audioOutputDeviceId === null,
                    })}
                    onSelect={onSelect}
                  >
                    <DropdownMenu.ItemIndicator>
                      <CheckIcon />
                    </DropdownMenu.ItemIndicator>
                    System Default
                  </DropdownMenu.RadioItem>
                }
                {listOfAudioOutputDevices.reduce(
                  (
                    acc: any,
                    { deviceId, label }: MediaDeviceInfo,
                    index: number,
                  ) => {
                    // ignore duplicate default device in chrome
                    if (deviceId !== 'default') {
                      acc.push(
                        <DropdownMenu.RadioItem
                          key={deviceId}
                          value={deviceId}
                          disabled={disabled}
                          className={radioItemStyles({
                            selected: deviceId === audioOutputDeviceId,
                          })}
                          onSelect={onSelect}
                        >
                          <DropdownMenu.ItemIndicator>
                            <CheckIcon />
                          </DropdownMenu.ItemIndicator>
                          {label || `Speaker ${index + 1}`}
                        </DropdownMenu.RadioItem>,
                      )
                    }
                    return acc
                  },
                  [],
                )}
              </DropdownMenu.RadioGroup>
            ) : (
              <DropdownMenu.Label className={userFeedbackStyles()}>
                {permissionStates.microphone === 'prompt' ? (
                  <Button onClick={() => askForPermission?.({ audio: true })}>
                    Request permission
                  </Button>
                ) : (
                  'Please grant permission'
                )}
              </DropdownMenu.Label>
            )
          ) : (
            <DropdownMenu.Label className={userFeedbackStyles()}>
              No devices detected
            </DropdownMenu.Label>
          )
        ) : (
          // eslint-disable-next-line
          // @ts-ignore
          <DropdownMenu.RadioGroup value={null}>
            <DropdownMenu.RadioItem
              // eslint-disable-next-line
              // @ts-ignore
              value={null}
              disabled={disabled}
              className={radioItemStyles({
                selected: true,
              })}
              onSelect={onSelect}
            >
              <DropdownMenu.ItemIndicator>
                <CheckIcon />
              </DropdownMenu.ItemIndicator>
              System Default
            </DropdownMenu.RadioItem>
          </DropdownMenu.RadioGroup>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

DeviceSelectors.displayName = 'DeviceSelectors'

const DEVICES_DROPDOWN_TRIGGER_ID = 'devices-dropdown-trigger'

const userFeedbackStyles = css({
  fontSize: '$2 !important',
  color: '$grey500 !important',
})

const radioItemStyles = css({
  '&[data-disabled]': {
    opacity: 0.5,
  },

  variants: {
    selected: {
      true: {},
      false: {
        paddingLeft: 'calc(25px + $2)',
      },
    },
  },
})
